import { Box, Link, Stack, Typography } from "@mui/material";
import { prefixClassName } from "@smartrr/shared/components/primitives";
import { DotSpinner } from "@smartrr/shared/customerPortal/components/DotSpinner";
import { ISODateString } from "@smartrr/shared/entities/ISODateString";
import { typedLocalize } from "@smartrr/shared/utils/sharedTranslations/localize";
import { sortByDate } from "@smartrr/shared/utils/sortBy";
import React, { useCallback, useEffect, useMemo, useState } from "react";

import { AquariusSelect } from "@customer-app/account/views/OperationAquarius/components/AquariusSelect";
import { openModal } from "@customer-app/state/actionCreators/modal";
import {
  useSmartrrCustomerDispatch,
  useSmartrrCustomerSelector,
} from "@customer-app/state/typedCustomerReduxHooks";

import { MOBILE_RESOLUTION, NoOrdersContainer, OrderHistoryCard, SelectContainer } from "./libs/constants";
import { fetchOrdersRecursivelyInPastYears, generateYearSelectMenuItems, handleChangeYear } from "./libs/utils";
import { OrderCard } from "./OrderCard";
import { useSmartrrTheme } from "../../../hooks/useSmartrrTheme";

export const OrderHistory = () => {
  const dispatch = useSmartrrCustomerDispatch();
  const currentYear = new Date().getFullYear();
  const { isLoading, orders } = useSmartrrCustomerSelector(state => state.orders);
  const theme = useSmartrrTheme();
  const showSectionTitles = theme.modernThemeSettings?.showHide.showSectionTitles;
  const [isMobile, setIsMobile] = useState<boolean>(false);

  const hasOrders = useSmartrrCustomerSelector(state => !!state.orders.orders.data.length);

  const [yearSelectorValue, setYearSelectorValue] = useState(currentYear);

  useEffect(() => {
    fetchOrdersRecursivelyInPastYears(currentYear, setYearSelectorValue);
  }, []);

  const pastOrders = useMemo(() => {
    return sortByDate([...orders.data] || [], order =>
      ISODateString.fromString(order.orderProcessedDate).toJSDate()
    );
  }, [orders, isLoading]);

  function handleWindowSizeChange() {
    if (window.innerWidth <= MOBILE_RESOLUTION) {
      setIsMobile(true);
    } else {
      setIsMobile(false);
    }
  }

  useEffect(() => {
    if (window.innerWidth <= MOBILE_RESOLUTION) {
      setIsMobile(true);
    }

    window.addEventListener("resize", handleWindowSizeChange);
    return () => {
      window.removeEventListener("resize", handleWindowSizeChange);
    };
  }, []);

  const modalHandler = useCallback(
    order => {
      dispatch(
        openModal("ORDER_HISTORY", {
          order,
        })
      );
    },
    [theme]
  );

  if (!hasOrders && !isLoading) {
    return (
      <Box display="flex" flexDirection="column" className={prefixClassName("order-history")}>
        <span className="anchor" id="order-history" />
        {showSectionTitles ? <Typography variant="h2">{typedLocalize("orderHistory.title")}</Typography> : null}
        <Stack
          direction="row"
          spacing={2}
          justifyContent="space-between"
          alignItems="center"
          pr={isMobile ? 0 : 2.25}
          mb={2}
          className={prefixClassName("order-history-heading")}
        >
          <Typography variant="body1">
            {typedLocalize("orderHistory.zeroStateForYear")}{" "}
            <Link href={typedLocalize("general.startShoppingLink")} underline="always" sx={{ color: "inherit" }}>
              {typedLocalize("general.startShopping")}
            </Link>
          </Typography>
          <SelectContainer>
            <AquariusSelect
              className={prefixClassName("year-select")}
              onChange={e => handleChangeYear(e, setYearSelectorValue)}
              value={yearSelectorValue}
              options={generateYearSelectMenuItems()}
            />
          </SelectContainer>
        </Stack>
      </Box>
    );
  }

  return (
    <div className={prefixClassName("order-history")}>
      <span className="anchor" id="order-history" />
      {showSectionTitles ? <Typography variant="h2">{typedLocalize("orderHistory.title")}</Typography> : null}
      <OrderHistoryCard isMobile={isMobile} className={prefixClassName("order-history-cards")}>
        <Stack
          direction="row"
          spacing={2}
          justifyContent="space-between"
          alignItems="center"
          pr={isMobile ? 0 : 2.25}
          mb={2}
          className={prefixClassName("order-history-heading")}
        >
          <Typography variant="h4" sx={{ marginBottom: "0px" }}>
            {typedLocalize("orderHistory.pastOrders")}
          </Typography>
          <AquariusSelect
            className={prefixClassName("year-select")}
            onChange={e => handleChangeYear(e, setYearSelectorValue)}
            value={yearSelectorValue}
            options={generateYearSelectMenuItems()}
          />
        </Stack>
        {isLoading ? (
          <Stack alignItems="center" justifyContent="center" p={4}>
            <DotSpinner />
          </Stack>
        ) : (
          <React.Fragment>
            {pastOrders.length ? (
              <Stack
                direction={isMobile ? "column" : "row"}
                spacing="10px"
                sx={{ overflowX: "auto", height: isMobile ? "375px" : "unset" }}
                className={prefixClassName("order-history-container")}
              >
                {pastOrders.map(order => (
                  <OrderCard
                    key={order.id}
                    order={order}
                    onClick={() => {
                      modalHandler(order);
                    }}
                  />
                ))}
              </Stack>
            ) : (
              <NoOrdersContainer alignItems="center" justifyContent="center">
                <Typography variant="caption1">{typedLocalize("orderHistory.zeroStateForYear")} </Typography>
              </NoOrdersContainer>
            )}
          </React.Fragment>
        )}
      </OrderHistoryCard>
    </div>
  );
};
